import { z } from 'zod';

/**
 * Zebra MX / DataWedge / EMDK extension config. v1 surface is intentionally
 * minimal; unmodelled fields will be added as customer requirements land.
 * Anything we don't model passes through as a no-op rather than a parse error.
 */
export const ZebraConfig = z.object({
  /** Apply a named DataWedge profile to scanner input. */
  dataWedgeProfile: z.string().min(1).max(80).optional(),
  /** Hardware key remap; key codes per Zebra MX docs. */
  keyRemap: z.record(z.string(), z.string()).optional(),
  /** Enable scanner LED feedback for successful decodes. */
  scannerFeedback: z.boolean().optional(),
  /** Battery hot-swap UI override (Zebra-specific power management). */
  hotSwapMode: z.enum(['auto', 'manual', 'disabled']).optional(),
});
export type ZebraConfig = z.infer<typeof ZebraConfig>;

/**
 * Samsung Knox extension config. Most Knox policies are managed by MobiControl
 * directly; this block covers launcher-relevant Samsung-only behaviors.
 */
export const SamsungConfig = z.object({
  /** Show Knox attestation status in diagnostic overlay. */
  showAttestationStatus: z.boolean().optional(),
  /** Hardware key intercepts (Bixby, side keys). */
  keyIntercepts: z.array(z.enum(['bixby', 'volumeUp', 'volumeDown', 'power'])).optional(),
  /** DeX mode behavior when device docked. */
  dexMode: z.enum(['kiosk', 'standard', 'disabled']).optional(),
});
export type SamsungConfig = z.infer<typeof SamsungConfig>;

/**
 * Honeywell EZConfig / Enterprise Browser-equivalent config.
 */
export const HoneywellConfig = z.object({
  /** Enable Honeywell scanner wedge with Zebra-equivalent semantics. */
  scannerWedgeEnabled: z.boolean().optional(),
  /** EZConfig profile name to apply on launch. */
  ezConfigProfile: z.string().min(1).max(80).optional(),
});
export type HoneywellConfig = z.infer<typeof HoneywellConfig>;

/**
 * Detection mode for OEM module loading. `auto` is production; `force*`
 * variants are testing/QA hatches; `none` disables OEM modules even if
 * entitled (useful for mixed-OEM deployments wanting stock behavior).
 */
export const DetectionMode = z.enum([
  'auto',
  'forceZebra',
  'forceSamsung',
  'forceHoneywell',
  'none',
]);
export type DetectionMode = z.infer<typeof DetectionMode>;

/**
 * OEM-specific configuration block. Schema accepts every sub-block at every
 * tier so the Builder can preview locked Enterprise panels; the runtime
 * ignores blocks without `oem.<vendor>` entitlement and matching detection.
 *
 * Cross-field rule: `force*` detection requires the corresponding sub-block.
 */
export const DeviceExtensions = z
  .object({
    detection: DetectionMode.optional(),
    zebra: ZebraConfig.optional(),
    samsung: SamsungConfig.optional(),
    honeywell: HoneywellConfig.optional(),
  })
  .refine((d) => d.detection !== 'forceZebra' || d.zebra !== undefined, {
    message: "detection 'forceZebra' requires device.zebra to be defined",
    path: ['zebra'],
  })
  .refine((d) => d.detection !== 'forceSamsung' || d.samsung !== undefined, {
    message: "detection 'forceSamsung' requires device.samsung to be defined",
    path: ['samsung'],
  })
  .refine((d) => d.detection !== 'forceHoneywell' || d.honeywell !== undefined, {
    message: "detection 'forceHoneywell' requires device.honeywell to be defined",
    path: ['honeywell'],
  });
export type DeviceExtensions = z.infer<typeof DeviceExtensions>;
